import { useEffect, useCallback, useRef } from "react";

import { supabase } from "@/lib/supabaseClient";
import { REALTIME_ENABLED } from "@/utils/env";

export interface RealtimeMetricsOptions {
  /** Callback to trigger when any metric-related table changes */
  onMetricsChange: () => void;
  /** Callback to trigger when activity-related data changes */
  onActivityChange: () => void;
  /** Whether to enable subscriptions (defaults to REALTIME_ENABLED) */
  enabled?: boolean;
}

export interface RealtimeMetricsReturn {
  /** Whether realtime is currently active */
  isRealtime: boolean;
  /** Force a manual refresh of both metrics and activity */
  refresh: () => void;
}

/**
 * Hook to subscribe to real-time changes for dashboard metrics.
 *
 * Subscribes to:
 * - donations table (revenue, avg gift, donation activity)
 * - donors table (donor count changes)
 * - campaigns table (active campaign count, campaign activity)
 *
 * Follows the established pattern from useNotifications.ts:
 * - Uses supabase.channel() for subscriptions
 * - Triggers callbacks on postgres_changes events
 * - Properly cleans up subscriptions on unmount
 * - Respects REALTIME_ENABLED feature flag
 *
 * @example
 * ```tsx
 * const { isRealtime, refresh } = useRealtimeMetrics({
 *   onMetricsChange: refreshPlatformMetrics,
 *   onActivityChange: refreshRecentActivity,
 * });
 * ```
 */
export function useRealtimeMetrics({
  onMetricsChange,
  onActivityChange,
  enabled = REALTIME_ENABLED,
}: RealtimeMetricsOptions): RealtimeMetricsReturn {
  // Use refs to store callbacks to avoid stale closures
  // This prevents re-subscribing when callbacks change
  const onMetricsChangeRef = useRef(onMetricsChange);
  const onActivityChangeRef = useRef(onActivityChange);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onMetricsChangeRef.current = onMetricsChange;
    onActivityChangeRef.current = onActivityChange;
  }, [onMetricsChange, onActivityChange]);

  // Manual refresh function
  const refresh = useCallback(() => {
    onMetricsChangeRef.current();
    onActivityChangeRef.current();
  }, []);

  useEffect(() => {
    // Early return if realtime is disabled
    if (!enabled) {
      return;
    }

    // Create a single channel for all platform metric subscriptions
    // This is more efficient than creating multiple channels
    const metricsChannel = supabase
      .channel("platform-metrics")
      // Subscribe to donations table - affects revenue, avg gift, activity feed
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "donations",
        },
        () => {
          // Donations affect both metrics and activity
          onMetricsChangeRef.current();
          onActivityChangeRef.current();
        },
      )
      // Subscribe to donors table - affects donor count
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "donors",
        },
        () => {
          // Donor changes only affect metrics, not activity feed
          onMetricsChangeRef.current();
        },
      )
      // Subscribe to campaigns table - affects active campaign count, activity feed
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "campaigns",
        },
        () => {
          // Campaign changes affect both metrics and activity
          onMetricsChangeRef.current();
          onActivityChangeRef.current();
        },
      )
      .subscribe();

    // Cleanup: unsubscribe when component unmounts or enabled changes
    return () => {
      void metricsChannel.unsubscribe();
    };
  }, [enabled]);

  return {
    isRealtime: enabled,
    refresh,
  };
}
